
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Search, Target } from 'lucide-react';

interface SearchConfig {
  semantic_threshold: number;
  max_results: number;
}

export const SemanticSearchConfig = () => {
  const [config, setConfig] = useState<SearchConfig>({
    semantic_threshold: 0.5,
    max_results: 10
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['semantic_threshold', 'semantic_max_results']);

      if (error) throw error;

      const newConfig = { ...config };
      data.forEach(item => {
        if (item.setting_key === 'semantic_threshold') {
          newConfig.semantic_threshold = parseFloat(item.setting_value) || 0.5;
        } else if (item.setting_key === 'semantic_max_results') {
          newConfig.max_results = parseInt(item.setting_value) || 10;
        }
      });

      setConfig(newConfig);
    } catch (error: any) {
      console.error('Error loading semantic search config:', error);
      toast({
        title: "Feil ved lasting av søkekonfigurasjon",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      // Save semantic threshold
      const { error: thresholdError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'semantic_threshold',
          setting_value: config.semantic_threshold.toString(),
          description: 'Threshold for semantic search similarity matching',
          updated_at: new Date().toISOString()
        });

      if (thresholdError) throw thresholdError;

      // Save max results
      const { error: maxResultsError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'semantic_max_results',
          setting_value: config.max_results.toString(),
          description: 'Maximum number of results for semantic search',
          updated_at: new Date().toISOString()
        });

      if (maxResultsError) throw maxResultsError;

      toast({
        title: "Søkekonfigurasjon lagret",
        description: "Endringene trer i kraft ved neste dokumentsøk",
      });
    } catch (error: any) {
      console.error('Error saving semantic search config:', error);
      toast({
        title: "Feil ved lagring",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const getThresholdLabel = (value: number) => {
    if (value <= 0.3) return 'Lav (Mer fleksibel)';
    if (value <= 0.6) return 'Middels (Balansert)';
    return 'Høy (Streng matching)';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Laster søkekonfigurasjon...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5" />
          Semantisk søkekonfigurasjon
        </CardTitle>
        <CardDescription>
          Juster hvordan dokumentsøket fungerer og matcher innhold
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-blue-500" />
            <Label className="text-sm font-medium">
              Søketreffer-terskel: {getThresholdLabel(config.semantic_threshold)}
            </Label>
          </div>
          <Slider
            value={[config.semantic_threshold]}
            onValueChange={([value]) => setConfig(prev => ({ ...prev, semantic_threshold: value }))}
            min={0.1}
            max={0.9}
            step={0.05}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Fleksibel (0.1)</span>
            <span>Balansert (0.5)</span>
            <span>Streng (0.9)</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Lavere verdier gir flere, men mindre presise resultater. Høyere verdier gir færre, men mer presise resultater.
          </p>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-green-500" />
            <Label className="text-sm font-medium">
              Maksimalt antall resultater: {config.max_results}
            </Label>
          </div>
          <Slider
            value={[config.max_results]}
            onValueChange={([value]) => setConfig(prev => ({ ...prev, max_results: value }))}
            min={5}
            max={25}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>5 resultater</span>
            <span>15 resultater</span>
            <span>25 resultater</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Hvor mange dokumenter som skal returneres ved søk
          </p>
        </div>

        <Button 
          onClick={saveConfig} 
          disabled={saving}
          className="w-full"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Lagrer...
            </>
          ) : (
            'Lagre søkekonfigurasjon'
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
